import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Table, Button, Input, Select, Modal, message as antMessage } from 'antd';
import audioService from '../services/audioService';
import { getDatabaseConfig } from '../services/databaseConfigService';
import './Components.css';

const { Option } = Select;

const BASE_URL = process.env.REACT_APP_CONFIG_API;
const API_BASE_URL = `${BASE_URL}/api`;

// Helper function to attach instance to params
function attachInstance(params = {}, instance) {
  if (!instance) return params;
  return { ...params, instance };
}

function Components() {
  // Instance selection state
  const [instanceList, setInstanceList] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [instanceLoading, setInstanceLoading] = useState(true);
  
  // Instance confirmation modal state
  const [instanceConfirmModal, setInstanceConfirmModal] = useState({
    isOpen: false,
    instance: null,
    friendlyName: ''
  });

  const [components, setComponents] = useState([]);
  const [selectedComponent, setSelectedComponent] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('1'); // Default to English
  const [componentData, setComponentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messagesData, setMessagesData] = useState({});
  const [promptsData, setPromptsData] = useState({});

  // Component Search tab state
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'search', or 'prompts'
  const [searchType, setSearchType] = useState('AI Prompt');
  const [searchGuid, setSearchGuid] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchLanguage, setSearchLanguage] = useState('All');

  // Prompts tab state
  const [promptGuidSearch, setPromptGuidSearch] = useState('');
  const [promptSearchResult, setPromptSearchResult] = useState(null);
  const [promptSearchLoading, setPromptSearchLoading] = useState(false);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [editedPromptText, setEditedPromptText] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [isValidJsonEdit, setIsValidJsonEdit] = useState(null); // null = not checked, true = valid, false = invalid
  const [jsonValidationErrorEdit, setJsonValidationErrorEdit] = useState('');
  const [showMediumSelectionModal, setShowMediumSelectionModal] = useState(false);
  const [selectedMediumsForSave, setSelectedMediumsForSave] = useState([]);
  // State for editing multiple prompts
  const [editingPromptIndex, setEditingPromptIndex] = useState(null); // Index of prompt being edited in multiple results
  const [editedMultiplePrompts, setEditedMultiplePrompts] = useState({}); // { index: editedText }
  const [jsonValidationMultiple, setJsonValidationMultiple] = useState({}); // { index: { isValid: true/false/null, error: '' } }
  const [savingPromptIndex, setSavingPromptIndex] = useState(null); // Index of prompt being saved
  
  // Add prompt modal state
  const [showAddPromptModal, setShowAddPromptModal] = useState(false);
  const [newPromptText, setNewPromptText] = useState('');
  const [newPromptMedium, setNewPromptMedium] = useState('');
  const [newPromptDescription, setNewPromptDescription] = useState('');
  const [addingPrompt, setAddingPrompt] = useState(false);
  const [isValidJson, setIsValidJson] = useState(null); // null = not checked, true = valid, false = invalid
  const [jsonValidationError, setJsonValidationError] = useState('');
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [savedPromptId, setSavedPromptId] = useState('');

  // Modal and audio states
  const [showDynamicModal, setShowDynamicModal] = useState(false);
  const [currentDynamicMessage, setCurrentDynamicMessage] = useState(null);
  const [populatedMessage, setPopulatedMessage] = useState('');
  const [isGeneratingDynamic, setIsGeneratingDynamic] = useState(false);
  const [dynamicAudioFile, setDynamicAudioFile] = useState(null);
  const [isPlayingDynamic, setIsPlayingDynamic] = useState(false);
  const dynamicAudioRef = useRef(null);
  
  // JSON view modal state
  const [showJsonModal, setShowJsonModal] = useState(false);
  
  // Components table modal state
  const [showComponentsTableModal, setShowComponentsTableModal] = useState(false);
  const [allComponents, setAllComponents] = useState([]);
  const [selectedComponents, setSelectedComponents] = useState(new Set());
  const [editingComponentData, setEditingComponentData] = useState({});
  const [savingComponentIds, setSavingComponentIds] = useState(new Set());
  const [loadingComponents, setLoadingComponents] = useState(false);

  // Existing audio playback states
  const [playingExistingAudio, setPlayingExistingAudio] = useState(null);
  const existingAudioRef = useRef(null);

  const languages = [
    { value: '1', label: 'English' },
    { value: '2', label: 'Spanish' },
    { value: '3', label: 'Punjabi' }
  ];

  // Audio functions - defined early for proper scoping
  function generateDynamicAudio() {
    if (!currentDynamicMessage) return;

    setIsGeneratingDynamic(true);
    audioService.generateDynamicAudio(
      currentDynamicMessage.originalText,
      populatedMessage
    ).then(result => {
      setDynamicAudioFile(result.audio_file);
      // Auto-play the generated audio
      playDynamicAudio(result.audio_file);
    }).catch(error => {
      console.error('Error generating dynamic audio:', error);
      alert('Failed to generate dynamic audio');
    }).finally(() => {
      setIsGeneratingDynamic(false);
    });
  }

  function playDynamicAudio(audioFile) {
    if (dynamicAudioRef.current) {
      dynamicAudioRef.current.pause();
    }

    const audioUrl = `${API_BASE_URL}/audio/file/${audioFile}`;
    dynamicAudioRef.current = new Audio(audioUrl);

    dynamicAudioRef.current.onended = () => {
      setIsPlayingDynamic(false);
    };

    dynamicAudioRef.current.play().then(() => {
      setIsPlayingDynamic(true);
    }).catch(error => {
      console.error('Error playing audio:', error);
      alert('Failed to play audio');
    });
  }

  function stopDynamicAudio() {
    if (dynamicAudioRef.current) {
      dynamicAudioRef.current.pause();
      dynamicAudioRef.current.currentTime = 0;
      setIsPlayingDynamic(false);
    }
  }

  // Fetch instance list on component mount
  useEffect(() => {
    const loadInstanceList = async () => {
      try {
        const data = await getDatabaseConfig();
        const connections = data?.availableConnections || [];
        setInstanceList(connections);
      } catch (err) {
        console.error('Error loading instance list:', err);
      } finally {
        setInstanceLoading(false);
      }
    };
    loadInstanceList();
  }, []);

  // Fetch components when instance is selected
  useEffect(() => {
    if (selectedInstance) {
      fetchComponents();
    } else {
      // Clear components when no instance is selected
      setComponents([]);
      setComponentData(null);
      setSelectedComponent('');
    }
  }, [selectedInstance]);

  const fetchComponents = async () => {
    try {
      setLoading(true);
      const params = attachInstance({}, selectedInstance);
      const response = await axios.get(`${API_BASE_URL}/audio/components/list`, { params });
      setComponents(response.data);
    } catch (err) {
      setError(err.message || 'Failed to fetch components');
      console.error('Error fetching components:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions to extract GUIDs from component JSON
  const extractMessageGuidsFromJson = (obj) => {
    const guids = [];
    const messageGuids = [];

    function traverse(obj) {
      if (typeof obj === 'string') {
        // Check if it's a GUID format - all GUIDs in the component could be messages
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(obj)) {
          guids.push(obj);
        }
      } else if (Array.isArray(obj)) {
        obj.forEach(item => traverse(item));
      } else if (obj && typeof obj === 'object') {
        Object.values(obj).forEach(value => traverse(value));
      }
    }

    traverse(obj);

    // For messages, we specifically want to extract from "message" fields
    // Also extract from fallback fields: invalidFallback1, invalidFallback2, emptyFallback1, emptyFallback2
    function extractMessages(obj, path = '') {
      if (typeof obj === 'object' && obj !== null) {
        Object.entries(obj).forEach(([key, value]) => {
          const currentPath = path ? `${path}.${key}` : key;
          const keyLower = key.toLowerCase();

          if (typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
            // If the key contains "message" or "Message", it's likely a message GUID
            // Also check for fallback fields: invalidFallback1, invalidFallback2, emptyFallback1, emptyFallback2
            if (keyLower.includes('message') || 
                keyLower.includes('fallback') ||
                keyLower === 'invalidfallback1' ||
                keyLower === 'invalidfallback2' ||
                keyLower === 'emptyfallback1' ||
                keyLower === 'emptyfallback2') {
              messageGuids.push(value);
              console.log(`Found message GUID in field "${key}": ${value}`);
            }
          } else if (typeof value === 'object') {
            extractMessages(value, currentPath);
          }
        });
      }
    }

    extractMessages(obj);

    console.log('All GUIDs found:', guids);
    console.log('Message GUIDs extracted:', messageGuids);

    return [...new Set(messageGuids)]; // Remove duplicates
  };

  const extractPromptGuidsFromJson = (obj) => {
    const guids = [];

    function traverse(obj) {
      if (typeof obj === 'string') {
        // Check if it's a GUID format - all GUIDs in the component could be prompts
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(obj)) {
          guids.push(obj);
        }
      } else if (Array.isArray(obj)) {
        obj.forEach(item => traverse(item));
      } else if (obj && typeof obj === 'object') {
        Object.values(obj).forEach(value => traverse(value));
      }
    }

    traverse(obj);

    // For prompts, we specifically want to extract from "aiPrompt" fields
    // Also check for variations: aiPrompt, ai_prompt, prompt, etc.
    const promptGuids = [];
    function extractPrompts(obj, path = '') {
      if (typeof obj === 'object' && obj !== null) {
        Object.entries(obj).forEach(([key, value]) => {
          const currentPath = path ? `${path}.${key}` : key;
          const keyLower = key.toLowerCase();

          if (typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
            // If the key contains "prompt" or "Prompt", it's likely a prompt GUID
            // Check for: aiPrompt, ai_prompt, prompt, etc.
            if (keyLower.includes('prompt') || 
                keyLower === 'aiprompt' ||
                keyLower === 'ai_prompt') {
              promptGuids.push(value);
              console.log(`Found prompt GUID in field "${key}": ${value}`);
            }
          } else if (typeof value === 'object') {
            extractPrompts(value, currentPath);
          }
        });
      }
    }

    extractPrompts(obj);

    console.log('All GUIDs found:', guids);
    console.log('Prompt GUIDs extracted:', promptGuids);

    return [...new Set(promptGuids)]; // Remove duplicates
  };

  // Define fetchMessagesForComponent before fetchComponentData
  const fetchMessagesForComponent = useCallback(async (componentJson) => {
    if (!selectedInstance) {
      console.log('No instance selected, skipping message fetch');
      return;
    }
    
    try {
      // Extract all message GUIDs from the component JSON
      const messageGuids = extractMessageGuidsFromJson(componentJson);
      console.log('Extracted message GUIDs:', messageGuids.length);

      if (messageGuids.length === 0) {
        console.log('No message GUIDs found in component');
        return;
      }

      const params = attachInstance({}, selectedInstance);
      console.log('Fetching messages with params:', { messageIdsCount: messageGuids.length, languageId: selectedLanguage, instance: selectedInstance });

      const response = await axios.post(`${API_BASE_URL}/audio/components/messages`, {
        messageIds: messageGuids,
        languageId: selectedLanguage
      }, {
        params: params
      });
      
      console.log('Messages response:', Object.keys(response.data).length, 'messages received');
      setMessagesData(response.data);
    } catch (err) {
      console.error('Error fetching messages:', err);
      console.error('Error details:', err.response?.data || err.message);
    }
  }, [selectedInstance, selectedLanguage]);

  // Define fetchPromptsForComponent before fetchComponentData
  const fetchPromptsForComponent = useCallback(async (componentJson) => {
    if (!selectedInstance) {
      console.log('No instance selected, skipping prompt fetch');
      return;
    }
    
    try {
      // Extract all prompt GUIDs from the component JSON
      const promptGuids = extractPromptGuidsFromJson(componentJson);
      console.log('Extracted prompt GUIDs:', promptGuids.length);

      if (promptGuids.length === 0) {
        console.log('No prompt GUIDs found in component');
        return;
      }

      const params = attachInstance({}, selectedInstance);
      console.log('Fetching prompts with params:', { promptIdsCount: promptGuids.length, instance: selectedInstance });

      // Note: wfc_prompt_master does not use language_id, so we don't send it
      const response = await axios.post(`${API_BASE_URL}/audio/components/prompts`, {
        promptIds: promptGuids
      }, {
        params: params
      });
      
      console.log('Prompts response:', Object.keys(response.data).length, 'prompts received');
      setPromptsData(response.data);
    } catch (err) {
      console.error('Error fetching prompts:', err);
      console.error('Error details:', err.response?.data || err.message);
      // Set empty prompts data on error so UI doesn't show loading forever
      setPromptsData({});
    }
  }, [selectedInstance, selectedLanguage]);

  const fetchComponentData = useCallback(async (componentDisplayName) => {
    if (!selectedInstance) {
      setError('Please select an instance first');
      return;
    }
    
    try {
      setLoading(true);
      const params = attachInstance({}, selectedInstance);
      const response = await axios.get(`${API_BASE_URL}/audio/components/data/${encodeURIComponent(componentDisplayName)}`, { params });
      setComponentData(response.data);

      // Fetch message and prompt details for all GUIDs in the component
      if (response.data && response.data.component_data) {
        await fetchMessagesForComponent(response.data.component_data);
        await fetchPromptsForComponent(response.data.component_data);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching component data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedInstance, fetchMessagesForComponent, fetchPromptsForComponent]);

  useEffect(() => {
    if (selectedComponent && selectedInstance) {
      fetchComponentData(selectedComponent);
    } else {
      setComponentData(null);
    }
  }, [selectedComponent, selectedInstance, fetchComponentData]);

  // Re-fetch messages when language changes (for already loaded component)
  useEffect(() => {
    if (selectedComponent && selectedInstance && componentData && componentData.component_data) {
      console.log(`🔄 Language changed to ${selectedLanguage}, re-fetching messages...`);
      fetchMessagesForComponent(componentData.component_data);
      fetchPromptsForComponent(componentData.component_data);
    }
  }, [selectedLanguage, selectedInstance, selectedComponent, componentData, fetchMessagesForComponent, fetchPromptsForComponent]);

  // Handle instance change with confirmation modal
  const handleInstanceChange = (instance) => {
    if (!instance) {
      setSelectedInstance(null);
      setComponents([]);
      setComponentData(null);
      return;
    }
    console.log('🔄 Switching to instance:', instance);
    // Find friendly name for display
    const friendly = instanceList.find((c) => c.INSTANCE === instance)?.INSTANCE_NAME || instance;

    // Show confirmation modal
    setInstanceConfirmModal({
      isOpen: true,
      instance,
      friendlyName: friendly
    });
  };

  // Confirm instance switch
  const confirmInstanceSwitch = () => {
    // Clear current data and apply new instance
    setSelectedInstance(instanceConfirmModal.instance);
    setComponents([]);
    setComponentData(null);
    setSelectedComponent('');
    setMessagesData({});
    setPromptsData({});
    setError(null);
    
    // Close modal
    setInstanceConfirmModal({
      isOpen: false,
      instance: null,
      friendlyName: ''
    });
  };

  // Cancel instance switch
  const cancelInstanceSwitch = () => {
    setInstanceConfirmModal({
      isOpen: false,
      instance: null,
      friendlyName: ''
    });
  };

  // Fetch all components with data
  const fetchAllComponents = async () => {
    if (!selectedInstance) {
      antMessage.warning('Please select an instance first');
      return;
    }

    setLoadingComponents(true);
    try {
      const params = attachInstance({}, selectedInstance);
      const response = await axios.get(`${API_BASE_URL}/audio/components/all`, { params });
      if (response.data.success) {
        setAllComponents(response.data.data);
        // Initialize editingComponentData with current component_data
        const editingData = {};
        response.data.data.forEach(comp => {
          // Handle null component_data
          if (comp.component_data === null || comp.component_data === undefined) {
            editingData[comp.component_key_id] = '{}';
          } else {
            editingData[comp.component_key_id] = JSON.stringify(comp.component_data, null, 2);
          }
        });
        setEditingComponentData(editingData);
      } else {
        antMessage.error('Failed to fetch components');
      }
    } catch (err) {
      console.error('Error fetching all components:', err);
      antMessage.error('Failed to fetch components: ' + (err.message || 'Unknown error'));
    } finally {
      setLoadingComponents(false);
    }
  };

  // Open components table modal
  const handleOpenComponentsTable = () => {
    if (!selectedInstance) {
      antMessage.warning('Please select an instance first');
      return;
    }
    setShowComponentsTableModal(true);
    setSelectedComponents(new Set());
    fetchAllComponents();
  };

  // Handle select/deselect component
  const handleToggleComponent = (componentKeyId) => {
    const newSelected = new Set(selectedComponents);
    if (newSelected.has(componentKeyId)) {
      newSelected.delete(componentKeyId);
    } else {
      newSelected.add(componentKeyId);
    }
    setSelectedComponents(newSelected);
  };

  // Handle select all
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedComponents(new Set(allComponents.map(c => c.component_key_id)));
    } else {
      setSelectedComponents(new Set());
    }
  };

  // Save operator data for a single component
  const handleSaveOperator = async (componentKeyId) => {
    if (!selectedInstance) {
      antMessage.error('Instance not selected');
      return;
    }

    const component = allComponents.find(c => c.component_key_id === componentKeyId);
    if (!component) {
      antMessage.error('Component not found');
      return;
    }

    // Parse the edited component_data
    let componentData;
    try {
      const dataString = editingComponentData[componentKeyId];
      if (!dataString || dataString.trim() === '' || dataString.trim() === 'null') {
        antMessage.warning(`Component "${component.component_display_name}" does not have valid component data. Skipping.`);
        return;
      }
      componentData = JSON.parse(dataString);
      // Handle case where JSON.parse returns null (e.g., if string was "null")
      if (componentData === null) {
        antMessage.warning(`Component "${component.component_display_name}" does not have valid component data. Skipping.`);
        return;
      }
    } catch (e) {
      antMessage.error('Invalid JSON in component data');
      return;
    }

    // Skip if componentData is not an object
    if (typeof componentData !== 'object' || Array.isArray(componentData)) {
      antMessage.warning(`Component "${component.component_display_name}" does not have valid component data. Skipping.`);
      return;
    }

    // Skip if operator section doesn't exist
    if (!componentData.operator || typeof componentData.operator !== 'object' || Array.isArray(componentData.operator)) {
      antMessage.warning(`Component "${component.component_display_name}" does not have an operator section. Skipping.`);
      return;
    }

    // Add emergencyDayHours and emergencyAfterHours if not present
    const emergencyDayHours = "15c12ad6-43c8-4028-bb3e-bb7f6cf98925";
    const emergencyAfterHours = "f042a3c9-c255-4165-a357-922a23188f22";

    if (!componentData.operator.emergencyDayHours) {
      componentData.operator.emergencyDayHours = emergencyDayHours;
    }
    if (!componentData.operator.emergencyAfterHours) {
      componentData.operator.emergencyAfterHours = emergencyAfterHours;
    }

    setSavingComponentIds(prev => new Set(prev).add(componentKeyId));

    try {
      const response = await axios.put(
        `${API_BASE_URL}/audio/components/${componentKeyId}/operator`,
        {
          instance: selectedInstance,
          operator: componentData.operator
        }
      );

      if (response.data.success) {
        antMessage.success(`Operator data saved for ${component.component_display_name}`);
        // Update the component in the list
        const updatedComponents = allComponents.map(c => 
          c.component_key_id === componentKeyId 
            ? { ...c, component_data: componentData }
            : c
        );
        setAllComponents(updatedComponents);
        // Update editing data
        setEditingComponentData(prev => ({
          ...prev,
          [componentKeyId]: JSON.stringify(componentData, null, 2)
        }));
      } else {
        antMessage.error('Failed to save operator data');
      }
    } catch (err) {
      console.error('Error saving operator data:', err);
      antMessage.error('Failed to save operator data: ' + (err.message || 'Unknown error'));
    } finally {
      setSavingComponentIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(componentKeyId);
        return newSet;
      });
    }
  };

  // Save operator data for selected components
  const handleSaveSelectedOperators = async () => {
    if (selectedComponents.size === 0) {
      antMessage.warning('Please select at least one component');
      return;
    }

    if (!selectedInstance) {
      antMessage.error('Instance not selected');
      return;
    }

    const emergencyDayHours = "15c12ad6-43c8-4028-bb3e-bb7f6cf98925";
    const emergencyAfterHours = "f042a3c9-c255-4165-a357-922a23188f22";

    // Process all selected components
    const savePromises = Array.from(selectedComponents).map(async (componentKeyId) => {
      const component = allComponents.find(c => c.component_key_id === componentKeyId);
      if (!component) return { skipped: true, reason: 'Component not found' };

      // Parse the edited component_data
      let componentData;
      try {
        const dataString = editingComponentData[componentKeyId];
        if (!dataString || dataString.trim() === '' || dataString.trim() === 'null') {
          return { skipped: true, reason: 'No valid component data', component: component.component_display_name };
        }
        componentData = JSON.parse(dataString);
        // Handle case where JSON.parse returns null (e.g., if string was "null")
        if (componentData === null) {
          return { skipped: true, reason: 'No valid component data', component: component.component_display_name };
        }
      } catch (e) {
        console.error(`Invalid JSON for component ${component.component_display_name}:`, e);
        return { skipped: true, reason: 'Invalid JSON', component: component.component_display_name };
      }

      // Skip if componentData is not an object
      if (typeof componentData !== 'object' || Array.isArray(componentData)) {
        return { skipped: true, reason: 'No valid component data', component: component.component_display_name };
      }

      // Skip if operator section doesn't exist
      if (!componentData.operator || typeof componentData.operator !== 'object' || Array.isArray(componentData.operator)) {
        return { skipped: true, reason: 'No operator section', component: component.component_display_name };
      }

      // Add emergencyDayHours and emergencyAfterHours if not present
      if (!componentData.operator.emergencyDayHours) {
        componentData.operator.emergencyDayHours = emergencyDayHours;
      }
      if (!componentData.operator.emergencyAfterHours) {
        componentData.operator.emergencyAfterHours = emergencyAfterHours;
      }

      setSavingComponentIds(prev => new Set(prev).add(componentKeyId));

      try {
        const response = await axios.put(
          `${API_BASE_URL}/audio/components/${componentKeyId}/operator`,
          {
            instance: selectedInstance,
            operator: componentData.operator
          }
        );

        if (response.data.success) {
          // Update the component in the list
          const updatedComponents = allComponents.map(c => 
            c.component_key_id === componentKeyId 
              ? { ...c, component_data: componentData }
              : c
          );
          setAllComponents(updatedComponents);
          // Update editing data
          setEditingComponentData(prev => ({
            ...prev,
            [componentKeyId]: JSON.stringify(componentData, null, 2)
          }));
          return { success: true, component: component.component_display_name };
        } else {
          return { success: false, component: component.component_display_name };
        }
      } catch (err) {
        console.error(`Error saving operator data for ${component.component_display_name}:`, err);
        return { success: false, component: component.component_display_name, error: err.message };
      } finally {
        setSavingComponentIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(componentKeyId);
          return newSet;
        });
      }
    });

    const results = await Promise.all(savePromises);
    const successCount = results.filter(r => r && r.success).length;
    const failCount = results.filter(r => r && !r.success && !r.skipped).length;
    const skippedCount = results.filter(r => r && r.skipped).length;

    if (successCount > 0) {
      antMessage.success(`Successfully saved operator data for ${successCount} component(s)`);
    }
    if (skippedCount > 0) {
      antMessage.warning(`Skipped ${skippedCount} component(s) (no operator section)`);
    }
    if (failCount > 0) {
      antMessage.error(`Failed to save operator data for ${failCount} component(s)`);
    }
  };

  // Handle GUID search
  const handleSearch = async () => {
    if (!searchGuid || !searchGuid.trim()) {
      antMessage.warning('Please enter a GUID to search');
      return;
    }

    if (!selectedInstance) {
      antMessage.warning('Please select an instance first');
      return;
    }

    setSearchLoading(true);
    try {
      const params = attachInstance({}, selectedInstance);
      
      // If "All" is selected for Message search, search all languages
      if (searchType === 'Message' && searchLanguage === 'All') {
        const languages = ['1', '2', '3', '4'];
        const languageNames = { '1': 'English', '2': 'Spanish', '3': 'Chinese', '4': 'Punjabi' };
        const allResults = [];
        
        // Make API calls for each language
        for (const langId of languages) {
          try {
            const payload = {
              guid: searchGuid.trim(),
              searchType: searchType,
              languageId: langId
            };
            const response = await axios.post(
              `${API_BASE_URL}/audio/components/search-guid`,
              payload,
              { params }
            );
            
            if (response.data.success && response.data.data) {
              // Add language info to each result
              const resultsWithLanguage = response.data.data.map(result => ({
                ...result,
                language: languageNames[langId] || `Language ${langId}`,
                languageId: langId
              }));
              allResults.push(...resultsWithLanguage);
            }
          } catch (error) {
            console.error(`Error searching for language ${langId}:`, error);
            // Continue with other languages even if one fails
          }
        }
        
        setSearchResults(allResults);
        if (allResults.length === 0) {
          antMessage.info('No results found for the specified GUID');
        } else {
          antMessage.success(`Found ${allResults.length} result(s) across all languages`);
        }
      } else {
        // Single language search (or AI Prompt search)
      const payload = {
        guid: searchGuid.trim(),
        searchType: searchType
      };
        if (searchType === 'Message' && searchLanguage && searchLanguage !== 'All') {
        payload.languageId = String(searchLanguage).trim();
      }
      const response = await axios.post(
        `${API_BASE_URL}/audio/components/search-guid`,
        payload,
        { params }
      );

      if (response.data.success) {
        setSearchResults(response.data.data || []);
        if (response.data.data && response.data.data.length === 0) {
          antMessage.info('No results found for the specified GUID');
        } else {
          antMessage.success(`Found ${response.data.data.length} result(s)`);
        }
      } else {
        antMessage.error(response.data.message || 'Search failed');
        setSearchResults([]);
        }
      }
    } catch (error) {
      console.error('Error searching GUID:', error);
      antMessage.error('Failed to search GUID: ' + (error.response?.data?.message || error.message));
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Copy UUID to clipboard
  const copyUUID = (uuid) => {
    if (!uuid) {
      antMessage.warning('No UUID to copy');
      return;
    }
    
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(uuid).then(() => {
        antMessage.success('UUID copied to clipboard');
      }).catch(err => {
        console.error('Copy failed', err);
        antMessage.error('Copy failed');
      });
    } else {
      // Fallback
      try {
        const textarea = document.createElement('textarea');
        textarea.value = uuid;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        antMessage.success('UUID copied to clipboard');
      } catch (err) {
        console.error('Copy fallback failed', err);
        antMessage.error('Copy failed');
      }
    }
  };

  // Copy prompt text to clipboard
  const copyPromptText = () => {
    if (!promptSearchResult || !promptSearchResult.promptText) {
      antMessage.warning('No prompt text to copy');
      return;
    }
    
    const textToCopy = promptSearchResult.promptText;
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(textToCopy).then(() => {
        antMessage.success('Prompt text copied to clipboard');
      }).catch(err => {
        console.error('Copy failed', err);
        antMessage.error('Copy failed');
      });
    } else {
      // Fallback
      try {
        const textarea = document.createElement('textarea');
        textarea.value = textToCopy;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        antMessage.success('Prompt text copied to clipboard');
      } catch (err) {
        console.error('Copy fallback failed', err);
        antMessage.error('Copy failed');
      }
    }
  };

  // Start editing prompt
  const startEditingPrompt = () => {
    if (promptSearchResult.isMultiple) {
      antMessage.warning('Cannot edit when multiple prompts with different texts exist');
      return;
    }
    setEditedPromptText(promptSearchResult.promptText);
    setIsEditingPrompt(true);
  };

  // Cancel editing
  const cancelEditingPrompt = () => {
    setIsEditingPrompt(false);
    setEditedPromptText('');
    setIsValidJsonEdit(null);
    setJsonValidationErrorEdit('');
  };

  // Validate and format JSON for edit mode
  const validateAndFormatJsonEdit = () => {
    if (!editedPromptText || !editedPromptText.trim()) {
      antMessage.warning('Please enter Prompt Text first');
      return;
    }

    try {
      const parsed = JSON.parse(editedPromptText.trim());
      // If valid, format it nicely
      const formatted = JSON.stringify(parsed, null, 2);
      setEditedPromptText(formatted);
      setIsValidJsonEdit(true);
      setJsonValidationErrorEdit('');
      antMessage.success('JSON is valid and formatted');
    } catch (error) {
      setIsValidJsonEdit(false);
      setJsonValidationErrorEdit(error.message);
      antMessage.error('JSON is not valid: ' + error.message);
    }
  };

  // Save prompt with selected mediums (shared function)
  const performSavePrompt = async (mediumsToSave) => {
    setSavingPrompt(true);
    try {
      const params = attachInstance({}, selectedInstance);
      const response = await axios.put(
        `${API_BASE_URL}/audio/prompts/${promptSearchResult.promptId}`,
        { 
          promptText: editedPromptText.trim(),
          mediums: mediumsToSave
        },
        { params }
      );

      if (response.data.success) {
        // Refresh the prompt result
        const refreshParams = attachInstance({}, selectedInstance);
        const refreshResponse = await axios.get(
          `${API_BASE_URL}/audio/prompts/${promptSearchResult.promptId}`,
          { params: refreshParams }
        );

        if (refreshResponse.data.success) {
          setPromptSearchResult({
            ...refreshResponse.data.data,
            promptText: refreshResponse.data.data.promptText
          });
        } else {
          // Fallback: update with edited text
          setPromptSearchResult({
            ...promptSearchResult,
            promptText: editedPromptText.trim()
          });
        }

        setIsEditingPrompt(false);
        setIsValidJsonEdit(null);
        setJsonValidationErrorEdit('');
        setShowMediumSelectionModal(false);
        setSelectedMediumsForSave([]);
        antMessage.success('Prompt updated successfully');
      } else {
        antMessage.error(response.data.message || 'Failed to update prompt');
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
      antMessage.error('Failed to save prompt: ' + (error.response?.data?.message || error.message));
    } finally {
      setSavingPrompt(false);
    }
  };

  // Save edited prompt - show medium selection modal only if multiple mediums exist
  const savePrompt = () => {
    if (!promptSearchResult || !editedPromptText.trim()) {
      antMessage.warning('Prompt text cannot be empty');
      return;
    }

    if (isValidJsonEdit !== true) {
      antMessage.warning('Please validate JSON before saving');
      return;
    }

    if (!selectedInstance) {
      antMessage.warning('Please select an instance first');
      return;
    }

    // Get available mediums from the prompt result
    const availableMediums = promptSearchResult.allMediums || [];
    if (availableMediums.length === 0) {
      antMessage.warning('No mediums found for this prompt');
      return;
    }

    // If only one medium, save directly without showing modal
    if (availableMediums.length === 1) {
      performSavePrompt(availableMediums);
    } else {
      // If multiple mediums, show selection modal
      setSelectedMediumsForSave([]);
      setShowMediumSelectionModal(true);
    }
  };

  // Confirm save with selected mediums (from modal)
  const confirmSavePrompt = async () => {
    if (selectedMediumsForSave.length === 0) {
      antMessage.warning('Please select at least one medium');
      return;
    }

    performSavePrompt(selectedMediumsForSave);
  };

  // Start editing a specific prompt in multiple results
  const startEditingMultiplePrompt = (index, promptText) => {
    setEditingPromptIndex(index);
    setEditedMultiplePrompts({
      ...editedMultiplePrompts,
      [index]: promptText
    });
    setJsonValidationMultiple({
      ...jsonValidationMultiple,
      [index]: { isValid: null, error: '' }
    });
  };

  // Cancel editing a specific prompt in multiple results
  const cancelEditingMultiplePrompt = (index) => {
    setEditingPromptIndex(null);
    const newEdited = { ...editedMultiplePrompts };
    delete newEdited[index];
    setEditedMultiplePrompts(newEdited);
    const newValidation = { ...jsonValidationMultiple };
    delete newValidation[index];
    setJsonValidationMultiple(newValidation);
  };

  // Validate JSON for a specific prompt in multiple results
  const validateJsonMultiplePrompt = (index) => {
    const promptText = editedMultiplePrompts[index];
    if (!promptText || !promptText.trim()) {
      antMessage.warning('Please enter Prompt Text first');
      return;
    }

    try {
      const parsed = JSON.parse(promptText.trim());
      const formatted = JSON.stringify(parsed, null, 2);
      setEditedMultiplePrompts({
        ...editedMultiplePrompts,
        [index]: formatted
      });
      setJsonValidationMultiple({
        ...jsonValidationMultiple,
        [index]: { isValid: true, error: '' }
      });
      antMessage.success('JSON is valid and formatted');
    } catch (error) {
      setJsonValidationMultiple({
        ...jsonValidationMultiple,
        [index]: { isValid: false, error: error.message }
      });
      antMessage.error('JSON is not valid: ' + error.message);
    }
  };

  // Save a specific prompt in multiple results
  const saveMultiplePrompt = async (index, medium) => {
    const promptText = editedMultiplePrompts[index];
    if (!promptText || !promptText.trim()) {
      antMessage.warning('Prompt text cannot be empty');
      return;
    }

    const validation = jsonValidationMultiple[index];
    if (!validation || validation.isValid !== true) {
      antMessage.warning('Please validate JSON before saving');
      return;
    }

    if (!selectedInstance) {
      antMessage.warning('Please select an instance first');
      return;
    }

    setSavingPromptIndex(index);
    try {
      const params = attachInstance({}, selectedInstance);
      const response = await axios.put(
        `${API_BASE_URL}/audio/prompts/${promptSearchResult.promptId}`,
        { 
          promptText: promptText.trim(),
          mediums: [medium]
        },
        { params }
      );

      if (response.data.success) {
        // Refresh the prompt result
        const refreshParams = attachInstance({}, selectedInstance);
        const refreshResponse = await axios.get(
          `${API_BASE_URL}/audio/prompts/${promptSearchResult.promptId}`,
          { params: refreshParams }
        );

        if (refreshResponse.data.success) {
          setPromptSearchResult(refreshResponse.data.data);
        }

        // Clear editing state for this prompt
        cancelEditingMultiplePrompt(index);
        antMessage.success('Prompt updated successfully');
      } else {
        antMessage.error(response.data.message || 'Failed to update prompt');
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
      antMessage.error('Failed to save prompt: ' + (error.response?.data?.message || error.message));
    } finally {
      setSavingPromptIndex(null);
    }
  };

  // Validate and format JSON
  const validateAndFormatJson = () => {
    if (!newPromptText || !newPromptText.trim()) {
      antMessage.warning('Please enter Prompt Text first');
      return;
    }

    try {
      const parsed = JSON.parse(newPromptText.trim());
      // If valid, format it nicely
      const formatted = JSON.stringify(parsed, null, 2);
      setNewPromptText(formatted);
      setIsValidJson(true);
      setJsonValidationError('');
      antMessage.success('JSON is valid and formatted');
    } catch (error) {
      setIsValidJson(false);
      setJsonValidationError(error.message);
      antMessage.error('JSON is not valid: ' + error.message);
    }
  };

  // Handle adding a new prompt
  const handleAddPrompt = async () => {
    if (!newPromptText || !newPromptText.trim()) {
      antMessage.warning('Please enter Prompt Text');
      return;
    }

    if (!newPromptDescription || !newPromptDescription.trim()) {
      antMessage.warning('Please enter Prompt Description');
      return;
    }

    if (!newPromptMedium || !newPromptMedium.trim()) {
      antMessage.warning('Please select Prompt Medium');
      return;
    }

    // Validate JSON before saving
    if (isValidJson === null) {
      antMessage.warning('Please validate JSON first');
      return;
    }

    if (isValidJson === false) {
      antMessage.error('Please fix JSON errors before saving');
      return;
    }

    if (!selectedInstance) {
      antMessage.warning('Please select an instance first');
      return;
    }

    setAddingPrompt(true);
    try {
      // Compress JSON (remove whitespace) before sending
      let compressedJson;
      try {
        const parsed = JSON.parse(newPromptText.trim());
        compressedJson = JSON.stringify(parsed);
      } catch (error) {
        antMessage.error('Error compressing JSON: ' + error.message);
        setAddingPrompt(false);
        return;
      }

      const params = attachInstance({}, selectedInstance);
      const response = await axios.post(
        `${API_BASE_URL}/audio/prompts`,
        {
          promptText: compressedJson,
          promptMedium: newPromptMedium.trim(),
          promptDescription: newPromptDescription.trim()
        },
        { params }
      );

      if (response.data.success) {
        const rowsAdded = response.data.data?.rowsAdded || 1;
        const promptId = response.data.data?.insertedRows?.[0]?.promptId || '';
        
        // Close add modal and show success modal with UUID
        setShowAddPromptModal(false);
        setNewPromptText('');
        setNewPromptMedium('');
        setNewPromptDescription('');
        setIsValidJson(null);
        setJsonValidationError('');
        
        // Show success modal with UUID
        setSavedPromptId(promptId);
        setShowSuccessModal(true);
      } else {
        antMessage.error(response.data.message || 'Failed to add prompt');
      }
    } catch (error) {
      console.error('Error adding prompt:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.details || error.response?.data?.error || error.response?.data?.message || error.message;
      antMessage.error('Failed to add prompt: ' + errorMessage);
    } finally {
      setAddingPrompt(false);
    }
  };

  // Handle prompt search by GUID/UUID - direct lookup in wfc_prompt_master
  const handlePromptSearch = async () => {
    if (!promptGuidSearch || !promptGuidSearch.trim()) {
      antMessage.warning('Please enter a prompt GUID/UUID to search');
      return;
    }

    if (!selectedInstance) {
      antMessage.warning('Please select an instance first');
      return;
    }

    setPromptSearchLoading(true);
    try {
      const params = attachInstance({}, selectedInstance);
      
      // Directly query wfc_prompt_master by prompt_id
      const response = await axios.get(
        `${API_BASE_URL}/audio/prompts/${promptGuidSearch.trim()}`,
        { params }
      );

      if (response.data.success && response.data.data) {
        const promptData = response.data.data;
        setPromptSearchResult(promptData);
        setIsEditingPrompt(false);
        setEditedPromptText(promptData.promptText || '');
        // Reset multiple prompt editing states
        setEditingPromptIndex(null);
        setEditedMultiplePrompts({});
        setJsonValidationMultiple({});
        setSavingPromptIndex(null);
        antMessage.success('Prompt found');
      } else {
        antMessage.info('No prompt found for the specified ID');
        setPromptSearchResult(null);
        // Reset multiple prompt editing states
        setEditingPromptIndex(null);
        setEditedMultiplePrompts({});
        setJsonValidationMultiple({});
        setSavingPromptIndex(null);
      }
    } catch (error) {
      console.error('Error searching prompt:', error);
      if (error.response?.status === 404) {
        antMessage.info('No prompt found for the specified ID');
      } else {
        antMessage.error('Failed to search prompt: ' + (error.response?.data?.message || error.message));
      }
      setPromptSearchResult(null);
    } finally {
      setPromptSearchLoading(false);
    }
  };

  // Download search results as CSV
  const downloadCSV = () => {
    if (searchResults.length === 0) {
      antMessage.warning('No results to download');
      return;
    }

    // Create CSV header
    const headers = ['Component Name', 'Step Name', 'Prompt GUID', 'Message', 'Prompt'];
    
    // Create CSV rows
    const csvRows = [
      headers.join(','),
      ...searchResults.map(result => {
        // Escape commas and quotes in CSV values
        const escapeCSV = (value) => {
          if (value === null || value === undefined) return '';
          const stringValue = String(value);
          // If value contains comma, quote, or newline, wrap in quotes and escape quotes
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        };

        return [
          escapeCSV(result.componentName),
          escapeCSV(result.stepName),
          escapeCSV(result.messageGuid),
          escapeCSV(result.message),
          escapeCSV(result.prompt)
        ].join(',');
      })
    ];

    // Create CSV content
    const csvContent = csvRows.join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `component_search_${searchType.replace(' ', '_')}_${searchGuid}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    antMessage.success('CSV file downloaded successfully');
  };

  const renderComponentCards = () => {
    if (!componentData || !componentData.component_data) return null;

    const data = componentData.component_data;
    const cards = [];

    // Add general card for operator and flags sections
    const generalSections = [];
    if (data.operator) {
      generalSections.push({ title: 'Operator', data: data.operator, isOperator: true });
    }
    if (data.flags) {
      generalSections.push({ title: 'Flags', data: data.flags, isFlags: true });
    }

    if (generalSections.length > 0) {
      cards.push(
        <div key="general" className="general-card">
          <h3 className="card-title">General Configuration</h3>
          <div className="general-sections">
            {generalSections.map((section, index) => (
              <div key={index} className="general-section">
                <h4 className="section-title">{section.title}</h4>
                <div className="section-content">
                  {renderGeneralSection(section)}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Group messages by code
    const codeGroups = {};

    // Process messages array to group by code
    if (data.messages && Array.isArray(data.messages)) {
      data.messages.forEach((messageItem) => {
        const code = messageItem.code || 'Unknown';
        if (!codeGroups[code]) {
          codeGroups[code] = [];
        }

        // Add main message
        if (messageItem.message) {
          const messageText = messagesData[messageItem.message]?.message_text;
          codeGroups[code].push({
            type: 'Message',
            guid: messageItem.message,
            message: messageText || '',
            hasAudio: messagesData[messageItem.message]?.hasAudio || false
          });
        }

        // Add AI prompt
        if (messageItem.aiPrompt) {
          const promptText = promptsData[messageItem.aiPrompt]?.prompt_text;
          codeGroups[code].push({
            type: 'AI Prompt',
            guid: messageItem.aiPrompt,
            message: promptText || ''
          });
        }

        // Add fallback messages
        if (messageItem.invalidFallback1) {
          const messageText = messagesData[messageItem.invalidFallback1]?.message_text;
          codeGroups[code].push({
            type: 'Invalid Fallback 1',
            guid: messageItem.invalidFallback1,
            message: messageText || '',
            hasAudio: messagesData[messageItem.invalidFallback1]?.hasAudio || false
          });
        }

        if (messageItem.invalidFallback2) {
          const messageText = messagesData[messageItem.invalidFallback2]?.message_text;
          codeGroups[code].push({
            type: 'Invalid Fallback 2',
            guid: messageItem.invalidFallback2,
            message: messageText || '',
            hasAudio: messagesData[messageItem.invalidFallback2]?.hasAudio || false
          });
        }

        if (messageItem.emptyFallback1) {
          const messageText = messagesData[messageItem.emptyFallback1]?.message_text;
          codeGroups[code].push({
            type: 'Empty Fallback 1',
            guid: messageItem.emptyFallback1,
            message: messageText || '',
            hasAudio: messagesData[messageItem.emptyFallback1]?.hasAudio || false
          });
        }

        if (messageItem.emptyFallback2) {
          const messageText = messagesData[messageItem.emptyFallback2]?.message_text;
          codeGroups[code].push({
            type: 'Empty Fallback 2',
            guid: messageItem.emptyFallback2,
            message: messageText || '',
            hasAudio: messagesData[messageItem.emptyFallback2]?.hasAudio || false
          });
        }
      });
    }

    // Function to populate dynamic parameters with reasonable values
  const populateDynamicParameters = (messageText) => {
    let populated = messageText;

    // Define parameter mappings with contextually appropriate values
    const parameterMappings = {
      // Personal Information
      'first name': 'Sarah',
      'last name': 'Johnson',
      'full name': 'Sarah Johnson',
      'patient name': 'Sarah Johnson',
      'name': 'Sarah Johnson',

      // Contact Information
      'phone number': '(555) 123-4567',
      'phone': '(555) 123-4567',
      'email': 'sarah.johnson@email.com',
      'email address': 'sarah.johnson@email.com',

      // Dates and Times
      'date': 'December 15, 2023',
      'appointment date': 'December 15, 2023',
      'time': '2:30 PM',
      'appointment time': '2:30 PM',

      // Medical/Healthcare Context
      'provider name': 'Dr. Michael Chen',
      'doctor name': 'Dr. Michael Chen',
      'physician name': 'Dr. Michael Chen',
      'department': 'Cardiology',
      'specialty': 'Cardiology',
      'location': 'Main Office',
      'office location': 'Main Office',
      'clinic': 'Downtown Medical Center',

      // Appointment Types
      'appointment type': 'Cardiology Consultation',
      'visit type': 'Follow-up Visit',
      'procedure': 'Cardiac Stress Test',

      // Medical Identifiers
      'patient id': 'PAT-12345',
      'medical record number': 'MRN-987654',
      'insurance id': 'INS-ABC123',

      // Addresses
      'address': '123 Main Street, Springfield',
      'street address': '123 Main Street',
      'city': 'Springfield',
      'state': 'IL',
      'zip code': '62701',
      'zip': '62701',

      // Emergency Contact
      'emergency contact name': 'Robert Johnson',
      'emergency contact phone': '(555) 987-6543',

      // Insurance
      'insurance provider': 'Blue Cross Blue Shield',
      'insurance plan': 'PPO Plus Plan',

      // Symptoms/Conditions (generic)
      'symptoms': 'chest pain and shortness of breath',
      'condition': 'hypertension',
      'diagnosis': 'mild hypertension',

      // Medications
      'medication': 'Lisinopril',
      'prescription': 'Lisinopril 10mg daily',

      // Test Results
      'test results': 'within normal limits',
      'lab results': 'all tests came back normal',

      // Follow-up
      'follow up date': 'January 15, 2024',
      'next appointment': 'January 15, 2024 at 2:30 PM'
    };

    // Function to get parameter value based on name
    const getParameterValue = (paramName) => {
      const lowerParamName = paramName.toLowerCase().trim();

      // Direct match
      if (parameterMappings[lowerParamName]) {
        return parameterMappings[lowerParamName];
      }

      // Partial match - check if parameter name contains key words
      for (const [key, value] of Object.entries(parameterMappings)) {
        if (lowerParamName.includes(key) || key.includes(lowerParamName)) {
          return value;
        }
      }

      // Generate reasonable defaults based on parameter patterns
      if (lowerParamName.includes('phone') || lowerParamName.includes('number')) {
        return '(555) 123-4567';
      }
      if (lowerParamName.includes('email')) {
        return 'patient@example.com';
      }
      if (lowerParamName.includes('date')) {
        return 'December 15, 2023';
      }
      if (lowerParamName.includes('time')) {
        return '2:30 PM';
      }
      if (lowerParamName.includes('name')) {
        return 'Sarah Johnson';
      }
      if (lowerParamName.includes('id') || lowerParamName.includes('number')) {
        return 'ID-12345';
      }

      // Final fallback
      return 'Sample Value';
    };

    // Replace parameters using regex
    populated = populated.replace(/\[([^\]]+)\]/g, (match, paramName) => {
      // Remove "Param" prefix if present
      const cleanParamName = paramName.replace(/^Param\s+/i, '').trim();
      return getParameterValue(cleanParamName);
    });

    return populated;
  };

  // Helper function to construct audio play URL from instance
  const getAudioPlayUrl = (audioFile) => {
    if (!selectedInstance || !audioFile) return null;
    
    // Find the instance config from instanceList
    const instanceConfig = instanceList.find(inst => inst.INSTANCE === selectedInstance);
    if (!instanceConfig) return null;
    
    let baseUrl = instanceConfig.INSTANCE;
    
    // Remove "/vsa/wf/inout" if it exists at the end
    if (baseUrl.endsWith('/vsa/wf/inout')) {
      baseUrl = baseUrl.replace(/\/vsa\/wf\/inout$/, '');
    }
    
    // Construct the play URL: <baseUrl>/vsa/wf/play?callSid=<audioFile>
    return `${baseUrl}/vsa/wf/play?callSid=${audioFile}`;
  };

  // Handle play audio button click
  const handlePlayAudio = async (messageId, rowData) => {
    if (rowData.hasAudio) {
      // Fetch audio_file from wfc_message_audios on-demand
      try {
        const params = attachInstance({}, selectedInstance);
        const response = await axios.get(`${API_BASE_URL}/audio/components/message/${messageId}/audio`, { params });
        
        const audioFile = response.data.audio_file;
        if (!audioFile) {
          alert('Audio file not found for this message');
          return;
        }

        // Construct play URL using instance URL
        const audioUrl = getAudioPlayUrl(audioFile);
        
        if (!audioUrl) {
          alert('Unable to construct audio URL. Please check instance configuration.');
          return;
        }

        if (playingExistingAudio === messageId) {
          // Stop current audio
          if (existingAudioRef.current) {
            existingAudioRef.current.pause();
            existingAudioRef.current.currentTime = 0;
          }
          setPlayingExistingAudio(null);
        } else {
          // Stop any currently playing audio first
          if (existingAudioRef.current) {
            existingAudioRef.current.pause();
            existingAudioRef.current.currentTime = 0;
          }

          // Start new audio
          console.log(`Playing audio from: ${audioUrl}`);
          existingAudioRef.current = new Audio(audioUrl);

          existingAudioRef.current.onended = () => {
            setPlayingExistingAudio(null);
          };

          existingAudioRef.current.onerror = () => {
            console.error('Error playing existing audio');
            setPlayingExistingAudio(null);
            alert('Failed to play audio file');
          };

          try {
            await existingAudioRef.current.play();
            setPlayingExistingAudio(messageId);
          } catch (error) {
            console.error('Error playing audio:', error);
            alert('Failed to play audio file');
          }
        }
      } catch (error) {
        console.error('Error fetching audio file:', error);
        if (error.response?.status === 404) {
          alert('Audio file not found for this message');
        } else {
          alert('Failed to load audio file');
        }
      }
    } else {
      // Show dynamic audio modal
      const originalMessage = rowData.message;
      const populated = populateDynamicParameters(originalMessage);

      setCurrentDynamicMessage({
        id: messageId,
        originalText: originalMessage,
        populatedText: populated,
        type: rowData.type
      });
      setPopulatedMessage(populated);
      setShowDynamicModal(true);
      setDynamicAudioFile(null);
      setIsPlayingDynamic(false);
    }
  };


  const renderCodeTable = (code, tableRows) => {
      if (tableRows.length === 0) return null;

      return (
        <div className="code-card">
          <h3 className="code-title">{code}</h3>
          <div className="code-table-container">
            <table className="code-table">
              <thead>
                <tr>
                  <th className="type-col">Type</th>
                  <th className="guid-col">GUID</th>
                  <th className="message-col">Message</th>
                  <th className="audio-col">Audio</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, index) => (
                  <tr key={index}>
                    <td className="type-cell">{row.type}</td>
                    <td className="guid-cell">
                      <code>{row.guid}</code>
                    </td>
                    <td className="message-cell">{row.message}</td>
                    <td className="audio-cell">
                      {row.hasAudio ? (
                        <button
                          className={`play-button has-audio ${playingExistingAudio === row.guid ? 'playing' : ''}`}
                          onClick={() => handlePlayAudio(row.guid, row)}
                          title={
                            playingExistingAudio === row.guid
                              ? 'Stop playing'
                              : 'Play audio'
                          }
                        >
                          {playingExistingAudio === row.guid ? '⏸️' : '▶️'}
                        </button>
                      ) : row.message && /\[<[^>]+>\]/.test(row.message) ? (
                        <button
                          className={`play-button dynamic-audio ${playingExistingAudio === row.guid ? 'playing' : ''}`}
                          onClick={() => handlePlayAudio(row.guid, row)}
                          title={
                            playingExistingAudio === row.guid
                              ? 'Stop playing'
                              : 'Generate dynamic audio'
                          }
                        >
                          {playingExistingAudio === row.guid ? '⏸️' : '🔄'}
                        </button>
                      ) : (
                        <span className="no-audio">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    };

    const codeCards = Object.entries(codeGroups).map(([code, tableRows]) =>
      renderCodeTable(code, tableRows)
    );

    if (codeCards.length === 0) {
      return <div className="no-data">No message data found in this component.</div>;
    }

    return <div className="code-cards-container">{cards.concat(codeCards)}</div>;
  };

  const renderGeneralSection = (section) => {
    const { data, isOperator, isFlags } = section;

    if (isOperator) {
      // For operator section, display key-value pairs with GUID resolution
      return (
        <div className="operator-config">
          {Object.entries(data).map(([key, value]) => (
            <div key={key} className="config-item">
              <strong className="config-key">{key}:</strong>
              <div className="config-value">
                {typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) ? (
                  <>
                    <code className="guid-display">{value}</code>
                    {messagesData[value] && (
                      <div className="message-preview">
                        {messagesData[value].message_text}
                      </div>
                    )}
                  </>
                ) : (
                  <span>{String(value)}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (isFlags) {
      // For flags section, display boolean/string values
      return (
        <div className="flags-config">
          {Object.entries(data).map(([key, value]) => (
            <div key={key} className="config-item">
              <strong className="config-key">{key}:</strong>
              <span className={`config-value ${typeof value === 'boolean' ? (value ? 'boolean-true' : 'boolean-false') : ''}`}>
                {String(value)}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };


  return (
    <div className="components-page">
      <div className="page-header">
        <h1>Components</h1>
        <div className="header-actions">
          {/* Instance Dropdown */}
          <select
            value={selectedInstance || ''}
            onChange={(e) => handleInstanceChange(e.target.value || null)}
            className="instance-select"
            disabled={instanceLoading}
          >
            <option value="">
              {instanceLoading ? 'Loading instances...' : 'Select Instance'}
            </option>
            {instanceList.map((conn) => (
              <option key={conn.INSTANCE} value={conn.INSTANCE}>
                {conn.INSTANCE_NAME || conn.INSTANCE}
              </option>
            ))}
          </select>
          
          {/* Components Table Button */}
          <button
            className="view-json-button"
            onClick={handleOpenComponentsTable}
            disabled={!selectedInstance}
            title="View All Components Table"
          >
            View Components Table
          </button>

          {/* Instance confirmation modal */}
          {instanceConfirmModal.isOpen && (
            <div className="modal-overlay" onClick={cancelInstanceSwitch}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
                <div className="modal-header">
                  <h2>Confirm Database Instance</h2>
                  <button className="close-btn" onClick={cancelInstanceSwitch}>✕</button>
                </div>
                <div className="modal-body">
                  <p>
                    Switch to instance "<strong>{instanceConfirmModal.friendlyName}</strong>"? This will reload component data for the selected database instance.
                  </p>
                </div>
                <div className="modal-footer">
                  <button onClick={cancelInstanceSwitch} className="btn-close">Cancel</button>
                  <button onClick={confirmInstanceSwitch} className="btn-confirm">Switch</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {!selectedInstance && (
        <div className="no-instance-message">
          <p>Please select a database instance to load components.</p>
        </div>
      )}

      {loading && selectedInstance && components.length === 0 && (
        <div className="loading">Loading components...</div>
      )}

      {error && selectedInstance && (
        <div className="error">Error: {error}</div>
      )}

      {selectedInstance && (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', flex: 1, minHeight: 0 }}>
          {/* Tabs */}
          <div className="component-tabs" style={{ marginBottom: '20px', borderBottom: '2px solid #e0e0e0', flexShrink: 0 }}>
            <button
              className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
              style={{
                padding: '10px 20px',
                marginRight: '10px',
                border: 'none',
                background: activeTab === 'details' ? '#667eea' : 'transparent',
                color: activeTab === 'details' ? 'white' : '#333',
                cursor: 'pointer',
                fontWeight: activeTab === 'details' ? 'bold' : 'normal'
              }}
            >
              Component Details
            </button>
            <button
              className={`tab-button ${activeTab === 'search' ? 'active' : ''}`}
              onClick={() => setActiveTab('search')}
              style={{
                padding: '10px 20px',
                marginRight: '10px',
                border: 'none',
                background: activeTab === 'search' ? '#667eea' : 'transparent',
                color: activeTab === 'search' ? 'white' : '#333',
                cursor: 'pointer',
                fontWeight: activeTab === 'search' ? 'bold' : 'normal'
              }}
            >
              Component Search
            </button>
            <button
              className={`tab-button ${activeTab === 'prompts' ? 'active' : ''}`}
              onClick={() => setActiveTab('prompts')}
              style={{
                padding: '10px 20px',
                border: 'none',
                background: activeTab === 'prompts' ? '#667eea' : 'transparent',
                color: activeTab === 'prompts' ? 'white' : '#333',
                cursor: 'pointer',
                fontWeight: activeTab === 'prompts' ? 'bold' : 'normal'
              }}
            >
              Prompts
            </button>
          </div>

          {/* Component Details Tab */}
          {activeTab === 'details' && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto', flex: 1, minHeight: 0 }}>
              <div className="component-selector">
                <div className="selector-group">
                  <label htmlFor="language-select">Language:</label>
                  <select
                    id="language-select"
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                  >
                    {languages.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {lang.label} ({lang.value})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="selector-group">
                  <label htmlFor="component-select">Component:</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <select
                      id="component-select"
                      value={selectedComponent}
                      onChange={(e) => setSelectedComponent(e.target.value)}
                    >
                      <option value="">Choose a component...</option>
                      {components.map((component) => (
                        <option key={component.component_display_name} value={component.component_display_name}>
                          {component.component_display_name}
                        </option>
                      ))}
                    </select>
                    <button
                      className="view-json-button"
                      onClick={() => setShowJsonModal(true)}
                      disabled={!selectedComponent || !componentData}
                      title="View Component JSON"
                    >
                      View
                    </button>
                  </div>
                </div>
              </div>

              {selectedComponent && componentData && (
                <div className="component-display">
                  <h2>{selectedComponent}</h2>
                  {renderComponentCards()}
                </div>
              )}
            </div>
          )}

          {/* Component Search Tab */}
          {activeTab === 'search' && (
            <div className="component-search">
              <div className="search-filters">
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <Select
                  value={searchType}
                  onChange={(val) => {
                    // Clear search input and results when switching between AI Prompt and Message
                    setSearchType(val);
                    setSearchGuid('');
                    setSearchResults([]);
                      // if switching away from Message, reset to default "All"
                      if (val !== 'Message') setSearchLanguage('All');
                  }}
                  style={{ width: 150 }}
                >
                  <Option value="AI Prompt">AI Prompt</Option>
                  <Option value="Message">Message</Option>
                </Select>
                <Input
                  placeholder="Enter GUID to search"
                  value={searchGuid}
                  onChange={(e) => setSearchGuid(e.target.value)}
                  style={{ width: 300 }}
                  onPressEnter={() => handleSearch()}
                />
                {searchType === 'Message' && (
                  <Select
                    value={searchLanguage}
                    onChange={(val) => setSearchLanguage(String(val))}
                    style={{ width: 320 }}
                    placeholder="select language"
                  >
                      <Option value="All">All</Option>
                    <Option value="1">English</Option>
                    <Option value="2">Spanish</Option>
                    <Option value="3">Chinese</Option>
                    <Option value="4">Punjabi</Option>
                  </Select>
                )}
                <Button
                  type="primary"
                  onClick={handleSearch}
                  loading={searchLoading}
                >
                  Search
                </Button>
                {searchResults.length > 0 && (
                  <Button
                    onClick={downloadCSV}
                    style={{ marginLeft: 'auto' }}
                  >
                    📥 Download CSV
                  </Button>
                )}
                </div>
              </div>

              <div className="search-table-container">
              {searchResults.length > 0 && (
                <Table
                  dataSource={searchResults}
                    // enable both horizontal and vertical scrolling so scrollbars meet at corner
                    scroll={{ x: 1200, y: 'calc(100vh - 450px)' }}
                    columns={(() => {
                      const baseColumns = [
                    {
                      title: <div style={{ textAlign: 'center' }}>Component Name</div>,
                      dataIndex: 'componentName',
                      key: 'componentName',
                      width: 160,
                      align: 'center'
                    },
                    {
                      title: <div style={{ textAlign: 'center' }}>Step Name</div>,
                      dataIndex: 'stepName',
                      key: 'stepName',
                      width: 160,
                      align: 'center'
                    },
                    {
                      title: <div style={{ textAlign: 'center' }}>Prompt GUID</div>,
                      dataIndex: 'messageGuid',
                      key: 'messageGuid',
                      width: 150,
                      render: (text) => <code style={{ fontSize: '12px' }}>{text}</code>,
                      align: 'center'
                        }
                      ];
                      
                      // Add Language column if "All" is selected
                      if (searchType === 'Message' && searchLanguage === 'All') {
                        baseColumns.push({
                          title: <div style={{ textAlign: 'center' }}>Language</div>,
                          dataIndex: 'language',
                          key: 'language',
                          width: 120,
                          align: 'center',
                          render: (text) => text || '-'
                        });
                      }
                      
                      // Add Message and Prompt columns
                      baseColumns.push(
                    {
                      title: <div style={{ textAlign: 'center' }}>Message</div>,
                      dataIndex: 'message',
                      key: 'message',
                          width: 350,
                          render: (text) => (
                            <div style={{ 
                              wordWrap: 'break-word', 
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                              lineHeight: '1.5'
                            }}>
                              {text}
                            </div>
                          )
                    },
                    {
                      title: <div style={{ textAlign: 'center' }}>Prompt</div>,
                      dataIndex: 'prompt',
                      key: 'prompt',
                      ellipsis: true,
                          width: 300,
                      render: (text) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: 420 }} title={text}>{text}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const toCopy = text || '';
                              if (!toCopy) {
                                antMessage.warning('Nothing to copy');
                                return;
                              }
                              if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                                navigator.clipboard.writeText(toCopy).then(() => {
                                  antMessage.success('Prompt copied');
                                }).catch(err => {
                                  console.error('Copy failed', err);
                                  antMessage.error('Copy failed');
                                });
                              } else {
                                // Fallback
                                try {
                                  const textarea = document.createElement('textarea');
                                  textarea.value = toCopy;
                                  document.body.appendChild(textarea);
                                  textarea.select();
                                  document.execCommand('copy');
                                  document.body.removeChild(textarea);
                                  antMessage.success('Prompt copied');
                                } catch (err) {
                                  console.error('Copy fallback failed', err);
                                  antMessage.error('Copy failed');
                                }
                              }
                            }}
                            className="copy-btn"
                            title="Copy prompt"
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '14px' }}
                          >
                            📋
                          </button>
                        </div>
                      )
                        }
                      );
                      
                      return baseColumns;
                    })()}
                  rowKey={(record, index) => `${record.componentName}-${record.stepName}-${index}`}
                    pagination={{ 
                      pageSize: 10,
                      showSizeChanger: false,
                      showTotal: (total) => `Total ${total} items`,
                      showQuickJumper: false,
                      position: ['bottomCenter']
                    }}
                />
              )}

              {searchResults.length === 0 && !searchLoading && searchGuid && (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                  No results found for the specified GUID.
                </div>
              )}
              </div>
            </div>
          )}

          {/* Prompts Tab */}
          {activeTab === 'prompts' && (
            <div className="component-search">
              <div className="search-filters">
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', padding: '20px' }}>
                  <Input
                    placeholder="Enter prompt GUID/UUID to search"
                    value={promptGuidSearch}
                    onChange={(e) => setPromptGuidSearch(e.target.value)}
                    style={{ width: 400 }}
                    onPressEnter={() => handlePromptSearch()}
                  />
                  <Button
                    type="primary"
                    onClick={() => handlePromptSearch()}
                    loading={promptSearchLoading}
                  >
                    Get Prompt
                  </Button>
                  <Button
                    type="default"
                    onClick={() => setShowAddPromptModal(true)}
                    style={{ marginLeft: 'auto' }}
                  >
                    ➕ Add Prompt
                  </Button>
                </div>
              </div>

              <div className="search-table-container" style={{ overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                {promptSearchResult && (
                  <div style={{ padding: '20px', flex: '1', overflow: 'auto' }}>
                    {promptSearchResult.isMultiple && promptSearchResult.prompts ? (
                      // Multiple results - different prompt texts
                      promptSearchResult.prompts.map((prompt, index) => (
                        <div key={index} style={{ marginBottom: '30px', padding: '15px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                          <div style={{ marginBottom: '10px' }}>
                            <strong>Prompt ID:</strong>
                            <code style={{ 
                              marginLeft: '10px', 
                              padding: '4px 8px', 
                              background: '#f0f9ff', 
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              {promptSearchResult.promptId}
                            </code>
                          </div>
                          <div style={{ marginBottom: '10px' }}>
                            <strong>Prompt Medium:</strong>
                            <span style={{ 
                              marginLeft: '10px', 
                              padding: '4px 8px', 
                              background: '#f0f9ff', 
                              borderRadius: '4px',
                              fontSize: '14px',
                              fontWeight: '500',
                              textTransform: 'capitalize'
                            }}>
                              {prompt.promptMedium === 'openAi' ? 'OpenAI' : prompt.promptMedium === 'gemini' ? 'Gemini' : prompt.promptMedium}
                            </span>
                          </div>
                          {prompt.promptDescription && (
                            <div style={{ marginBottom: '10px' }}>
                              <strong>Prompt Description:</strong>
                              <span style={{ 
                                marginLeft: '10px', 
                                padding: '4px 8px', 
                                background: '#f0f9ff', 
                                borderRadius: '4px',
                                fontSize: '14px'
                              }}>
                                {prompt.promptDescription}
                              </span>
                            </div>
                          )}
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                              <strong>Prompt Text:</strong>
                              <div style={{ display: 'flex', gap: '10px' }}>
                                {editingPromptIndex !== index ? (
                                  <>
                                    <Button
                                      size="small"
                                      onClick={() => startEditingMultiplePrompt(index, prompt.promptText)}
                                      style={{ fontSize: '12px' }}
                                    >
                                      ✏️ Edit
                                    </Button>
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        const textToCopy = prompt.promptText;
                                        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                                          navigator.clipboard.writeText(textToCopy).then(() => {
                                            antMessage.success('Prompt text copied to clipboard');
                                          }).catch(err => {
                                            console.error('Copy failed', err);
                                            antMessage.error('Copy failed');
                                          });
                                        } else {
                                          try {
                                            const textarea = document.createElement('textarea');
                                            textarea.value = textToCopy;
                                            document.body.appendChild(textarea);
                                            textarea.select();
                                            document.execCommand('copy');
                                            document.body.removeChild(textarea);
                                            antMessage.success('Prompt text copied to clipboard');
                                          } catch (err) {
                                            console.error('Copy fallback failed', err);
                                            antMessage.error('Copy failed');
                                          }
                                        }
                                      }}
                                      style={{ fontSize: '12px' }}
                                    >
                                      📋 Copy
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      size="small"
                                      type="default"
                                      onClick={() => validateJsonMultiplePrompt(index)}
                                      style={{ fontSize: '12px' }}
                                    >
                                      Validate JSON
                                    </Button>
                                    <Button
                                      size="small"
                                      type="primary"
                                      onClick={() => saveMultiplePrompt(index, prompt.promptMedium)}
                                      loading={savingPromptIndex === index}
                                      disabled={!jsonValidationMultiple[index] || jsonValidationMultiple[index].isValid !== true}
                                      style={{ fontSize: '12px' }}
                                    >
                                      💾 Save
                                    </Button>
                                    <Button
                                      size="small"
                                      onClick={() => cancelEditingMultiplePrompt(index)}
                                      disabled={savingPromptIndex === index}
                                      style={{ fontSize: '12px' }}
                                    >
                                      ❌ Cancel
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                            {editingPromptIndex === index && jsonValidationMultiple[index]?.isValid === true && (
                              <div style={{ 
                                padding: '8px 12px', 
                                marginBottom: '8px', 
                                backgroundColor: '#d1fae5', 
                                color: '#065f46', 
                                borderRadius: '4px',
                                fontSize: '14px',
                                border: '1px solid #10b981'
                              }}>
                                ✓ JSON is valid
                              </div>
                            )}
                            {editingPromptIndex === index && jsonValidationMultiple[index]?.isValid === false && (
                              <div style={{ 
                                padding: '8px 12px', 
                                marginBottom: '8px', 
                                backgroundColor: '#fee2e2', 
                                color: '#991b1b', 
                                borderRadius: '4px',
                                fontSize: '14px',
                                border: '1px solid #ef4444'
                              }}>
                                ✗ JSON is not valid: {jsonValidationMultiple[index]?.error}
                              </div>
                            )}
                            <textarea
                              value={editingPromptIndex === index ? (editedMultiplePrompts[index] || '') : (prompt.promptText || '')}
                              onChange={(e) => {
                                if (editingPromptIndex === index) {
                                  setEditedMultiplePrompts({
                                    ...editedMultiplePrompts,
                                    [index]: e.target.value
                                  });
                                  // Reset validation when user edits
                                  if (jsonValidationMultiple[index]) {
                                    setJsonValidationMultiple({
                                      ...jsonValidationMultiple,
                                      [index]: { isValid: null, error: '' }
                                    });
                                  }
                                }
                              }}
                              readOnly={editingPromptIndex !== index}
                              style={{
                                width: '100%',
                                minHeight: '200px',
                                padding: '15px',
                                background: editingPromptIndex === index ? 'white' : '#f9fafb',
                                borderRadius: '4px',
                                border: editingPromptIndex === index && jsonValidationMultiple[index]?.isValid === false ? '1px solid #ef4444' : editingPromptIndex === index && jsonValidationMultiple[index]?.isValid === true ? '1px solid #10b981' : '1px solid #d1d5db',
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                lineHeight: '1.6',
                                resize: 'vertical',
                                cursor: editingPromptIndex === index ? 'text' : 'default'
                              }}
                            />
                            <div style={{ marginTop: '5px', fontSize: '11px', color: '#999' }}>
                              Text length: {(editingPromptIndex === index ? editedMultiplePrompts[index] : prompt.promptText)?.length || 0} characters
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      // Single result - same prompt text
                      <div style={{ marginBottom: '20px', padding: '15px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                        <div style={{ marginBottom: '10px' }}>
                          <strong>Prompt ID:</strong>
                          <code style={{ 
                            marginLeft: '10px', 
                            padding: '4px 8px', 
                            background: '#f0f9ff', 
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            {promptSearchResult.promptId}
                          </code>
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                          <strong>Prompt Medium:</strong>
                          <span style={{ 
                            marginLeft: '10px', 
                            padding: '4px 8px', 
                            background: '#f0f9ff', 
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            {promptSearchResult.promptMedium || 'N/A'}
                          </span>
                        </div>
                        {promptSearchResult.promptDescription && (
                          <div style={{ marginBottom: '10px' }}>
                            <strong>Prompt Description:</strong>
                            <span style={{ 
                              marginLeft: '10px', 
                              padding: '4px 8px', 
                              background: '#f0f9ff', 
                              borderRadius: '4px',
                              fontSize: '14px'
                            }}>
                              {promptSearchResult.promptDescription}
                            </span>
                          </div>
                        )}
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <strong>Prompt Text:</strong>
                            <div style={{ display: 'flex', gap: '10px' }}>
                              {!isEditingPrompt ? (
                                <>
                                  <Button
                                    size="small"
                                    onClick={startEditingPrompt}
                                    style={{ fontSize: '12px' }}
                                  >
                                    ✏️ Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={copyPromptText}
                                    style={{ fontSize: '12px' }}
                                  >
                                    📋 Copy
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="small"
                                    type="default"
                                    onClick={validateAndFormatJsonEdit}
                                    style={{ fontSize: '12px' }}
                                  >
                                    Validate JSON
                                  </Button>
                                  <Button
                                    size="small"
                                    type="primary"
                                    onClick={savePrompt}
                                    loading={savingPrompt}
                                    disabled={isValidJsonEdit !== true}
                                    style={{ fontSize: '12px' }}
                                  >
                                    💾 Save
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={cancelEditingPrompt}
                                    disabled={savingPrompt}
                                    style={{ fontSize: '12px' }}
                                  >
                                    ❌ Cancel
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                          {isEditingPrompt && isValidJsonEdit === true && (
                            <div style={{ 
                              padding: '8px 12px', 
                              marginBottom: '8px', 
                              backgroundColor: '#d1fae5', 
                              color: '#065f46', 
                              borderRadius: '4px',
                              fontSize: '14px',
                              border: '1px solid #10b981'
                            }}>
                              ✓ JSON is valid
                            </div>
                          )}
                          {isEditingPrompt && isValidJsonEdit === false && (
                            <div style={{ 
                              padding: '8px 12px', 
                              marginBottom: '8px', 
                              backgroundColor: '#fee2e2', 
                              color: '#991b1b', 
                              borderRadius: '4px',
                              fontSize: '14px',
                              border: '1px solid #ef4444'
                            }}>
                              ✗ JSON is not valid: {jsonValidationErrorEdit}
                            </div>
                          )}
                           <textarea
                             key={promptSearchResult.promptId} // Force re-render when prompt changes
                             value={isEditingPrompt ? editedPromptText : (promptSearchResult?.promptText || '')}
                             onChange={(e) => {
                               if (isEditingPrompt) {
                                 setEditedPromptText(e.target.value);
                                 // Reset validation when user edits
                                 if (isValidJsonEdit !== null) {
                                   setIsValidJsonEdit(null);
                                   setJsonValidationErrorEdit('');
                                 }
                               }
                             }}
                             readOnly={!isEditingPrompt}
                             style={{
                               width: '100%',
                               minHeight: '200px',
                               padding: '15px',
                               background: isEditingPrompt ? 'white' : '#f9fafb',
                               borderRadius: '4px',
                               border: isEditingPrompt && isValidJsonEdit === false ? '1px solid #ef4444' : isEditingPrompt && isValidJsonEdit === true ? '1px solid #10b981' : '1px solid #d1d5db',
                               fontFamily: 'monospace',
                               fontSize: '14px',
                               lineHeight: '1.6',
                               resize: 'vertical',
                               cursor: isEditingPrompt ? 'text' : 'default'
                             }}
                             placeholder={promptSearchResult?.promptText ? '' : 'Prompt text will appear here...'}
                           />
                           {promptSearchResult && (
                             <div style={{ marginTop: '5px', fontSize: '11px', color: '#999' }}>
                               Text length: {promptSearchResult.promptText?.length || 0} characters | 
                               Medium: {promptSearchResult.promptMedium || 'N/A'}
                             </div>
                           )}
                        </div>
                      </div>
                    )}

                    {promptSearchResult.components && promptSearchResult.components.length > 0 && (
                      <div>
                        <h3 style={{ marginBottom: '15px', color: '#1f2937' }}>Used in Components:</h3>
                        <Table
                          dataSource={promptSearchResult.components}
                          scroll={{ x: 600 }}
                          columns={[
                            {
                              title: <div style={{ textAlign: 'center' }}>Component Name</div>,
                              dataIndex: 'componentName',
                              key: 'componentName',
                              width: 300,
                              align: 'center'
                            },
                            {
                              title: <div style={{ textAlign: 'center' }}>Step Name</div>,
                              dataIndex: 'stepName',
                              key: 'stepName',
                              width: 300,
                              align: 'center'
                            }
                          ]}
                          rowKey={(record, index) => `${record.componentName}-${record.stepName}-${index}`}
                          pagination={{ 
                            pageSize: 10,
                            showSizeChanger: false,
                            showTotal: (total) => `Total ${total} items`,
                            showQuickJumper: false,
                            position: ['bottomCenter']
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {!promptSearchResult && !promptSearchLoading && promptGuidSearch && (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    No prompt found for the specified GUID.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Add Prompt Modal */}
          {showAddPromptModal && (
            <div className="modal-overlay" onClick={() => {
              setShowAddPromptModal(false);
              setNewPromptText('');
              setNewPromptMedium('');
              setNewPromptDescription('');
              setIsValidJson(null);
              setJsonValidationError('');
            }}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '90%' }}>
                <div className="modal-header">
                  <h2>Add New Prompt</h2>
                  <button className="close-btn" onClick={() => {
                    setShowAddPromptModal(false);
                    setNewPromptText('');
                    setNewPromptMedium('');
                    setNewPromptDescription('');
                    setIsValidJson(null);
                    setJsonValidationError('');
                  }}>✕</button>
                </div>
                <div className="modal-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                        Prompt Description: <span style={{ color: 'red' }}>*</span>
                      </label>
                      <Input
                        placeholder="Enter prompt description"
                        value={newPromptDescription}
                        onChange={(e) => setNewPromptDescription(e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                        Prompt Medium: <span style={{ color: 'red' }}>*</span>
                      </label>
                      <Select
                        value={newPromptMedium}
                        onChange={(val) => setNewPromptMedium(val)}
                        style={{ width: '100%' }}
                        placeholder="Select prompt medium"
                      >
                        <Option value="gemini">Gemini</Option>
                        <Option value="openAi">OpenAI</Option>
                        <Option value="gemini / openAi">Gemini / OpenAI</Option>
                      </Select>
                    </div>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                        <label style={{ fontWeight: '600' }}>
                          Prompt Text (JSON): <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Button
                          type="default"
                          onClick={validateAndFormatJson}
                          style={{ marginLeft: '10px' }}
                        >
                          Validate JSON
                        </Button>
                      </div>
                      {isValidJson === true && (
                        <div style={{ 
                          padding: '8px 12px', 
                          marginBottom: '8px', 
                          backgroundColor: '#d1fae5', 
                          color: '#065f46', 
                          borderRadius: '4px',
                          fontSize: '14px',
                          border: '1px solid #10b981'
                        }}>
                          ✓ JSON is valid
                        </div>
                      )}
                      {isValidJson === false && (
                        <div style={{ 
                          padding: '8px 12px', 
                          marginBottom: '8px', 
                          backgroundColor: '#fee2e2', 
                          color: '#991b1b', 
                          borderRadius: '4px',
                          fontSize: '14px',
                          border: '1px solid #ef4444'
                        }}>
                          ✗ JSON is not valid: {jsonValidationError}
                        </div>
                      )}
                      <textarea
                        value={newPromptText}
                        onChange={(e) => {
                          setNewPromptText(e.target.value);
                          // Reset validation when user edits
                          if (isValidJson !== null) {
                            setIsValidJson(null);
                            setJsonValidationError('');
                          }
                        }}
                        placeholder='Enter prompt text in JSON format, e.g., {"key": "value"}'
                        style={{
                          width: '100%',
                          minHeight: '200px',
                          padding: '10px',
                          border: isValidJson === false ? '1px solid #ef4444' : isValidJson === true ? '1px solid #10b981' : '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontFamily: 'monospace',
                          fontSize: '14px',
                          lineHeight: '1.6',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button onClick={() => {
                    setShowAddPromptModal(false);
                    setNewPromptText('');
                    setNewPromptMedium('');
                    setNewPromptDescription('');
                    setIsValidJson(null);
                    setJsonValidationError('');
                  }} className="btn-close">
                    Cancel
                  </button>
                  <button 
                    onClick={handleAddPrompt} 
                    className="btn-confirm"
                    disabled={addingPrompt || !newPromptText.trim() || !newPromptDescription.trim() || !newPromptMedium || isValidJson !== true}
                  >
                    {addingPrompt ? 'Adding...' : 'Add Prompt'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Medium Selection Modal for Save */}
          <Modal
            title="Select Medium(s) to Update"
            open={showMediumSelectionModal}
            onOk={confirmSavePrompt}
            onCancel={() => {
              setShowMediumSelectionModal(false);
              setSelectedMediumsForSave([]);
            }}
            okText="Save"
            cancelText="Cancel"
            confirmLoading={savingPrompt}
            width={500}
          >
            <div style={{ padding: '20px 0' }}>
              <p style={{ marginBottom: '15px', fontSize: '14px', color: '#6b7280' }}>
                Select which medium(s) to update with the new prompt text:
              </p>
              {promptSearchResult && promptSearchResult.allMediums && promptSearchResult.allMediums.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {promptSearchResult.allMediums.map((medium) => (
                    <label key={medium} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '4px', backgroundColor: selectedMediumsForSave.includes(medium) ? '#f0f9ff' : 'white' }}>
                      <input
                        type="checkbox"
                        checked={selectedMediumsForSave.includes(medium)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedMediumsForSave([...selectedMediumsForSave, medium]);
                          } else {
                            setSelectedMediumsForSave(selectedMediumsForSave.filter(m => m !== medium));
                          }
                        }}
                        style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: '500', textTransform: 'capitalize' }}>
                        {medium === 'openAi' ? 'OpenAI' : medium === 'gemini' ? 'Gemini' : medium}
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#ef4444', fontSize: '14px' }}>No mediums available for this prompt</p>
              )}
            </div>
          </Modal>

          {/* Success Modal with UUID */}
          <Modal
            title="Prompt Added Successfully"
            open={showSuccessModal}
            onOk={() => setShowSuccessModal(false)}
            onCancel={() => setShowSuccessModal(false)}
            okText="Close"
            cancelButtonProps={{ style: { display: 'none' } }}
            width={600}
          >
            <div style={{ padding: '20px 0' }}>
              <p style={{ marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>
                Your prompt has been saved successfully!
              </p>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                  Prompt UUID:
                </label>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px',
                  padding: '12px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db'
                }}>
                  <code style={{ 
                    flex: 1, 
                    fontFamily: 'monospace', 
                    fontSize: '14px', 
                    color: '#1f2937',
                    wordBreak: 'break-all',
                    padding: '0'
                  }}>
                    {savedPromptId}
                  </code>
                  <Button
                    type="default"
                    onClick={() => copyUUID(savedPromptId)}
                    style={{ 
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    📋 Copy
                  </Button>
                </div>
              </div>
              <p style={{ marginTop: '15px', fontSize: '14px', color: '#6b7280' }}>
                Save this UUID for future reference. You can use it to search for this prompt.
              </p>
            </div>
          </Modal>
        </div>
      )}

      {loading && selectedComponent && (
        <div className="loading">Loading component data...</div>
      )}

      {error && selectedComponent && (
        <div className="error">Error loading component: {error}</div>
      )}

      {/* JSON View Modal */}
      {showJsonModal && componentData && (
        <div className="modal-overlay" onClick={() => setShowJsonModal(false)}>
          <div className="json-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Component JSON: {selectedComponent}</h3>
              <button
                className="modal-close"
                onClick={() => setShowJsonModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <pre className="json-content">
                {JSON.stringify(componentData, null, 2)}
              </pre>
            </div>

            <div className="modal-footer">
              <button
                className="btn-close"
                onClick={() => setShowJsonModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Components Table Modal */}
      {showComponentsTableModal && (
        <div className="modal-overlay" onClick={() => setShowComponentsTableModal(false)}>
          <div className="components-table-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>All Components</h3>
              <button
                className="modal-close"
                onClick={() => setShowComponentsTableModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="components-table-actions">
                <button
                  className="btn-confirm"
                  onClick={handleSaveSelectedOperators}
                  disabled={selectedComponents.size === 0 || savingComponentIds.size > 0}
                >
                  Save Selected ({selectedComponents.size})
                </button>
              </div>

              {loadingComponents ? (
                <div className="loading">Loading components...</div>
              ) : (
                <div className="components-table-container">
                  <table className="components-table">
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}>
                          <input
                            type="checkbox"
                            checked={selectedComponents.size === allComponents.length && allComponents.length > 0}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                          />
                        </th>
                        <th>Component Name</th>
                        <th>Component ID</th>
                        <th>Component Data</th>
                        <th style={{ width: '150px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allComponents.map((component) => (
                        <tr key={component.component_key_id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedComponents.has(component.component_key_id)}
                              onChange={() => handleToggleComponent(component.component_key_id)}
                            />
                          </td>
                          <td>{component.component_display_name}</td>
                          <td>{component.component_key_id}</td>
                          <td>
                            <textarea
                              className="component-data-editor"
                              value={editingComponentData[component.component_key_id] || ''}
                              onChange={(e) => setEditingComponentData(prev => ({
                                ...prev,
                                [component.component_key_id]: e.target.value
                              }))}
                              rows={8}
                              style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }}
                            />
                          </td>
                          <td>
                            <button
                              className="save-operator-button"
                              onClick={() => handleSaveOperator(component.component_key_id)}
                              disabled={savingComponentIds.has(component.component_key_id)}
                            >
                              {savingComponentIds.has(component.component_key_id) ? 'Saving...' : 'SaveOperator'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn-close"
                onClick={() => setShowComponentsTableModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dynamic Audio Modal */}
      {showDynamicModal && currentDynamicMessage && (
        <div className="modal-overlay" onClick={() => setShowDynamicModal(false)}>
          <div className="dynamic-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Dynamic Audio Generation</h3>
              <button
                className="modal-close"
                onClick={() => setShowDynamicModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <div className="message-section">
                <h4>Original Message:</h4>
                <div className="original-message">
                  {currentDynamicMessage.originalText}
                </div>
              </div>

              <div className="message-section">
                <h4>Populated Message:</h4>
                <textarea
                  className="populated-message-input"
                  value={populatedMessage}
                  onChange={(e) => setPopulatedMessage(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="audio-controls">
                <button
                  className="generate-audio-btn"
                  onClick={() => generateDynamicAudio()}
                  disabled={isGeneratingDynamic}
                >
                  {isGeneratingDynamic ? 'Generating...' : '🎵 Generate Audio'}
                </button>

                {dynamicAudioFile && (
                  <button
                    className={`play-audio-btn ${isPlayingDynamic ? 'playing' : ''}`}
                    onClick={isPlayingDynamic ? () => stopDynamicAudio() : () => playDynamicAudio(dynamicAudioFile)}
                  >
                    {isPlayingDynamic ? '⏸️ Stop' : '▶️ Play'}
                  </button>
                )}
              </div>

              {dynamicAudioFile && (
                <div className="audio-info">
                  <small>Audio file: {dynamicAudioFile}</small>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Components;
